import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'; // Import useMemo
import { X } from 'lucide-react'; // Import X
import { motion } from 'framer-motion';
import ValidatedInput from '../shared/ValidatedInput';
import { DEFAULT_VALIDATION_CONFIG, validateAndSanitizeInput } from '../../utils/validation/inputValidation'; // Corrected import path
import { UnifiedSchema } from '../../types/schema';
import { createUnifiedSchema } from '../../services/schemaDefinitionApiService';
import useEscapeKey from '../../hooks/useEscapeKey'; // Import useEscapeKey
import { useClickOutside } from '../../hooks/useClickOutside'; // Import useClickOutside
import { LoadingSpinner } from '../LoadingSpinner'; // Import LoadingSpinner

import { useInputValidation } from '../../utils/validation/inputValidation'; // Import useInputValidation

interface SaveSchemaAsPublishedDialogProps {
  open: boolean;
  onClose: () => void;
  currentSchema: UnifiedSchema | null;
  onSaveSuccess: (newSchema: UnifiedSchema) => void;
  existingSchemaNames: string[];
}

const SaveSchemaAsPublishedDialog: React.FC<SaveSchemaAsPublishedDialogProps> = ({
  open,
  onClose,
  currentSchema,
  onSaveSuccess,
  existingSchemaNames,
}) => {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useClickOutside<HTMLDivElement>(onClose);
  useEscapeKey(onClose);

  const validationConfig = useMemo(() => DEFAULT_VALIDATION_CONFIG.projectName, []); // Define validationConfig here

  const {
    value: newSchemaName,
    setValue: setNewSchemaName,
    isValid: isNameValid,
    errorMessage: nameErrorMessage,
    handleChange: handleNameChange,
    handleBlur: handleNameBlur,
  } = useInputValidation(currentSchema ? `${currentSchema.name} (Published)` : '', validationConfig); // Pass validationConfig

  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNewSchemaName(currentSchema ? `${currentSchema.name} (Published)` : '');
      setDescription(''); // Clear description on open
      setDuplicateNameError(null); // Clear duplicate name error
      // Focus the input when the dialog opens
      setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select(); // Select text for easy overwrite
      }, 100);
    }
  }, [open, currentSchema, setNewSchemaName]);

  const handleSave = useCallback(async () => {
    if (!currentSchema) return;

    const sanitizedDescription = validateAndSanitizeInput(description, DEFAULT_VALIDATION_CONFIG.projectDescription);

    // Perform duplicate name check
    if (existingSchemaNames.includes(newSchemaName)) {
      setDuplicateNameError('A schema with this name already exists. Please choose a different name.');
      return;
    } else {
      setDuplicateNameError(null);
    }

    // Ensure basic validation from hook passes
    if (!isNameValid) {
      // Trigger blur to show validation message if not already shown
      handleNameBlur();
      return;
    }

    setIsSaving(true);
    try {
      const newSchema: UnifiedSchema = {
        ...currentSchema,
        id: `unified-schema-${Date.now()}-${Math.random()}`, // Generate a new ID
        name: newSchemaName, // Use the validated and sanitized name from the hook
        description: sanitizedDescription, // Add description
        isNew: true, // Mark as new
        status: 'published', // Set status to published
      };

      const result = await createUnifiedSchema(newSchema);
      if (result) {
        onSaveSuccess(result);
        onClose(); // Close dialog on success
      } else {
        setDuplicateNameError('Failed to save schema as published. Please try again.');
      }
    } catch (error) {
      console.error('Error saving schema as published:', error);
      setDuplicateNameError('An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  }, [currentSchema, newSchemaName, existingSchemaNames, isNameValid, onSaveSuccess, onClose, handleNameBlur]); // Added handleNameBlur to dependencies

  // Combine errors for display
  const displayError = nameErrorMessage || duplicateNameError;

  if (!open) {
    return null;
  }

  return (
        <motion.div
          className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
          initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        ref={dialogContentRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (newSchemaName.trim() && isNameValid && !duplicateNameError) {
              handleSave();
            }
          }
        }}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Save Schema as New Published Schema</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <ValidatedInput
              ref={nameInputRef}
              id="new-schema-name"
              label="New Schema Name"
              value={newSchemaName}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              validationConfig={validationConfig}
              placeholder="Enter new schema name"
              errorMessage={displayError || undefined} // Pass undefined if no error
            />
            <ValidatedInput
              id="new-schema-description"
              label="Description (Optional)"
              isTextarea={true}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setDescription(e.target.value);
              }}
              validationConfig={DEFAULT_VALIDATION_CONFIG.projectDescription}
              placeholder="Enter a brief description for the new schema"
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 rounded-b-lg border-t border-gray-200 dark:border-gray-800">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !newSchemaName.trim() || !isNameValid || !!duplicateNameError}
              className={`
                px-4 py-2 text-sm font-medium rounded-md inline-flex items-center gap-2
                ${
                  (isSaving || !newSchemaName.trim() || !isNameValid || !!duplicateNameError)
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }
              `}
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" className="text-gray-500 dark:text-gray-400" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save as Published'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SaveSchemaAsPublishedDialog;
